import React, { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Box, Button, CircularProgress, Container, TextField, Typography } from '@mui/material'

import { getCompanySlug, useFetchSearchableFields, useFlowSlug } from 'utils/flow'
import { useSearchSessions } from 'utils/flowSession'

import SearchResults from './SearchResults'

const SessionSearchPage = (): JSX.Element => {
  const [cursor, setCursor] = useState<string | null>(null)
  const flowSlug = useFlowSlug() ? useFlowSlug().split('/')[0] : null
  const companySlug = getCompanySlug()
  const [searchParams, setSearchParams] = useState({
    flow_slug: flowSlug ?? '',
    company_slug: companySlug,
  })
  const [inputValues, setInputValues] = useState<Record<string, string>>({})

  const {
    data: fields,
    isLoading: fieldsLoading,
    error: fieldsError,
  } = useFetchSearchableFields(flowSlug)
  const { data, isLoading, error } = useSearchSessions(searchParams, cursor)

  const handleInputChange = (name: string, value: string): void => {
    setInputValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSearch = (event?: React.FormEvent): void => {
    if (event) {
      event.preventDefault() // Prevent default form submission behavior
    }

    setCursor(null)
    setSearchParams((previousValue) => ({
      ...previousValue,
      ...inputValues,
    }))
  }

  const handlePagination = (newCursor: string | null): void => {
    setCursor(newCursor) // Update cursor to navigate to the next/previous page
  }

  if (fieldsLoading) {
    return <CircularProgress />
  }

  if (!flowSlug || fieldsError) {
    return (
      <Navigate
        to="/pages/not-found"
        replace
        state={{
          title: 'Not Found',
          statusCode: 404,
          text: 'Not Found Page',
        }}
      />
    )
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ p: 3, boxShadow: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Typography variant="h4" gutterBottom>
          {companySlug} Search Data - Flow: {flowSlug}
        </Typography>
        <Box
          component="form"
          sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2, bgcolor: 'background.paper' }}
          onSubmit={handleSearch}
        >
          {/* Render dynamic inputs */}
          {fields?.map((field: any) => (
            <Box key={field.id} sx={{ mb: 2 }}>
              <TextField
                label={field.element.props.label || field.element.name}
                variant="outlined"
                fullWidth
                value={inputValues[field.element.name] || ''}
                onChange={(e) => handleInputChange(field.element.name, e.target.value)}
              />
            </Box>
          ))}
          <Button type="submit" variant="contained" color="primary" onClick={handleSearch}>
            Search
          </Button>
        </Box>

        {isLoading && <CircularProgress sx={{ mt: 2 }} />}

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error.message}
          </Typography>
        )}

        {/* Search Results */}
        {data && (
          <>
            <SearchResults results={data.results} flowSlug={flowSlug} searchFields={fields} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                disabled={!data.previous}
                onClick={() =>
                  handlePagination(
                    data.previous ? new URL(data.previous).searchParams.get('cursor') : null
                  )
                }
              >
                Previous
              </Button>
              <Button
                disabled={!data.next}
                onClick={() =>
                  handlePagination(data.next ? new URL(data.next).searchParams.get('cursor') : null)
                }
              >
                Next
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  )
}

export default SessionSearchPage
